import { Node, mergeAttributes } from "@tiptap/core";
import Suggestion, { type SuggestionProps as TipTapSuggestionProps } from "@tiptap/suggestion";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { Transaction, EditorState } from "@tiptap/pm/state";
import { PluginKey } from "@tiptap/pm/state";

// Unique plugin key for song reference suggestions
const SongReferencePluginKey = new PluginKey("song-reference-suggestion");

export interface SongReferenceNodeAttrs {
  id: string;
  title: string;
}

// Extended props with type discriminator
export interface SongReferenceSuggestionProps extends Omit<TipTapSuggestionProps, 'editor'> {
  type: "mention" | "song";
}

export interface SongReferenceSuggestionHandlers {
  onStart?: (props: SongReferenceSuggestionProps) => void;
  onUpdate?: (props: SongReferenceSuggestionProps) => void;
  onExit?: () => void;
  onKeyDown?: (props: { event: KeyboardEvent }) => boolean;
}

export interface SongReferenceOptions {
  suggestion?: SongReferenceSuggestionHandlers;
}

export const SongReferenceExtension = Node.create<SongReferenceOptions>({
  name: "songReference",

  group: "inline",

  inline: true,

  selectable: false,

  atom: true,

  addOptions() {
    return {
      suggestion: undefined,
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-id"),
        renderHTML: (attributes: Record<string, unknown>) => ({
          "data-id": attributes.id,
        }),
      },
      title: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-title"),
        renderHTML: (attributes: Record<string, unknown>) => ({
          "data-title": attributes.title,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: `span[data-type="${this.name}"]`,
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }: { node: ProseMirrorNode; HTMLAttributes: Record<string, unknown> }) {
    // Slugify the title for display
    const slugifiedTitle = (node.attrs.title as string)
      .replace(/\s+/g, "-")
      .replace(/[^\w-]/g, "");

    return [
      "span",
      mergeAttributes(
        { "data-type": this.name, class: "song-reference" },
        HTMLAttributes
      ),
      `#${slugifiedTitle}`,
    ];
  },

  renderText({ node }: { node: ProseMirrorNode }) {
    // For plain text extraction, use the format: #Title[song:ULID]
    const slugifiedTitle = (node.attrs.title as string)
      .replace(/\s+/g, "-")
      .replace(/[^\w-]/g, "");
    return `#${slugifiedTitle}[song:${node.attrs.id}]`;
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }: { tr: Transaction; state: EditorState }) => {
          let isSongRef = false;
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node: ProseMirrorNode, pos: number) => {
            if (node.type.name === this.name) {
              isSongRef = true;
              tr.insertText("", pos, pos + node.nodeSize);
              return false;
            }
          });

          return isSongRef;
        }),
    };
  },

  addProseMirrorPlugins() {
    const handlers = this.options.suggestion;

    return [
      Suggestion({
        editor: this.editor,
        pluginKey: SongReferencePluginKey,
        char: "#",
        command: () => {
          // Command is handled externally via suggestion handlers
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const type = state.schema.nodes[this.name];
          const allow = !!$from.parent.type.contentMatch.matchType(type);
          return allow;
        },
        render: () => {
          return {
            onStart: (props: TipTapSuggestionProps) => {
              handlers?.onStart?.({ ...props, type: "song" });
            },
            onUpdate: (props: TipTapSuggestionProps) => {
              handlers?.onUpdate?.({ ...props, type: "song" });
            },
            onExit: () => {
              handlers?.onExit?.();
            },
            onKeyDown: (props: { event: KeyboardEvent }) => {
              return handlers?.onKeyDown?.(props) ?? false;
            },
          };
        },
      }),
    ];
  },
});
